import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useDashboards, useProjectStatuses, useTeams, useUsers } from '../../api/queries'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import type { Dashboard, DashboardFilter } from '../../api/types'

const filterTypes = [
  { value: 'team', label: 'Team' },
  { value: 'project_manager', label: 'Projektleder' },
  { value: 'status', label: 'Status' },
  { value: 'category', label: 'Kategori' }
]

/**
 * Dashboards bestemmer hvad hver TV-skærm viser (kravspec afsnit 25).
 * Filtre af samme type kombineres med ELLER, forskellige typer med OG.
 */
export function DashboardsPage() {
  const { data, isLoading, error } = useDashboards()

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data || data.length === 0) return <EmptyState>Ingen dashboards.</EmptyState>

  return (
    <div className="stack" style={{ gap: 16 }}>
      {data.map((dashboard) => (
        <DashboardCard key={dashboard.id} dashboard={dashboard} />
      ))}
    </div>
  )
}

function DashboardCard({ dashboard }: { dashboard: Dashboard }) {
  const queryClient = useQueryClient()
  const { data: teams } = useTeams()
  const { data: users } = useUsers()
  const { data: statuses } = useProjectStatuses()

  const [name, setName] = useState(dashboard.name)
  const [maxProjects, setMaxProjects] = useState(dashboard.maxProjects)
  const [refresh, setRefresh] = useState(dashboard.refreshIntervalSeconds)
  const [showStaffing, setShowStaffing] = useState(dashboard.showStaffing)
  // Kortet over sagerne i stedet for tabellen (beslutning 111).
  const [showMap, setShowMap] = useState(dashboard.showMap ?? false)
  // Hvem dagens bemanding viser. Tom = alle (beslutning 076).
  const [staffingUserIds, setStaffingUserIds] = useState<number[]>(dashboard.staffingUserIds ?? [])

  // Kun dem, der kan vælges, tæller og sendes. En konto, der stod valgt og siden er skjult eller
  // deaktiveret, ville ellers hænge usynligt i listen (Simon, 2026-09-29).
  const selectable = new Set((users ?? []).filter((u) => u.isActive && !u.isHidden).map((u) => u.id))
  const chosenStaffing = users ? staffingUserIds.filter((id) => selectable.has(id)) : staffingUserIds

  function toggleStaffingUser(userId: number) {
    setStaffingUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]
    )
    setSaved(false)
  }
  const [filters, setFilters] = useState<DashboardFilter[]>(dashboard.filters)
  const [newFilterType, setNewFilterType] = useState('team')
  const [newFilterValue, setNewFilterValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  function optionsFor(type: string) {
    if (type === 'team') return teams?.map((t) => ({ value: String(t.id), label: t.name })) ?? []
    if (type === 'project_manager') return users?.map((u) => ({ value: String(u.id), label: u.displayName })) ?? []
    if (type === 'status') return statuses?.map((s) => ({ value: String(s.id), label: s.name })) ?? []
    return []
  }

  function labelFor(filter: DashboardFilter) {
    const option = optionsFor(filter.filterType).find((o) => o.value === filter.value)
    return option?.label ?? filter.value
  }

  async function save() {
    setError(null)
    try {
      await api.put(`/api/dashboards/${dashboard.id}`, {
        name,
        description: dashboard.description,
        layout: dashboard.layout,
        refreshIntervalSeconds: refresh,
        showStatistics: dashboard.showStatistics,
        showRecentActivity: dashboard.showRecentActivity,
        showStaffing,
        showMap,
        staffingUserIds: chosenStaffing,
        maxProjects,
        isDefault: dashboard.isDefault,
        filters: filters.map((f) => ({ filterType: f.filterType, value: f.value, include: f.include }))
      })
      await queryClient.invalidateQueries({ queryKey: ['dashboards'] })
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme dashboardet.')
    }
  }

  return (
    <Card
      title={dashboard.name}
      actions={
        <span className="tiny muted">
          {dashboard.screenCount} skærm(e){dashboard.isDefault ? ' · standard' : ''}
        </span>
      }
    >
      <div className="form-grid">
        <label className="field">
          <span className="field__label">Navn</span>
          <input value={name} onChange={(e) => { setName(e.target.value); setSaved(false) }} />
        </label>
        <label className="field">
          <span className="field__label">Maks. antal projekter (0 = alle)</span>
          <input type="number" value={maxProjects} onChange={(e) => { setMaxProjects(Number(e.target.value)); setSaved(false) }} />
        </label>
        <label className="field">
          <span className="field__label">Fuld genindlæsning hvert (sek.)</span>
          <input type="number" value={refresh} onChange={(e) => { setRefresh(Number(e.target.value)); setSaved(false) }} />
        </label>
      </div>

      {/* Kortet over sagerne i stedet for tabellen (beslutning 111). */}
      <label className="row" style={{ gap: 8, alignItems: 'center', margin: '4px 0 8px' }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={showMap}
          onChange={(e) => { setShowMap(e.target.checked); setSaved(false) }}
        />
        <span className="small">
          Vis sagerne på et kort i stedet for tabellen
          {showMap && (
            <span className="tiny muted" style={{ display: 'block' }}>
              Kortet viser alle åbne sager, der passer til filtrene - også flere end maks. antal projekter.
              Nålen står på sagens egen adresse, ellers kundens. Rød nål = deadline overskredet.
            </span>
          )}
        </span>
      </label>

      {/* Dagens bemanding ved siden af sagerne (beslutning 076). */}
      <label className="row" style={{ gap: 8, alignItems: 'center', margin: '4px 0 12px' }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={showStaffing}
          onChange={(e) => { setShowStaffing(e.target.checked); setSaved(false) }}
        />
        <span className="small">
          Vis dagens bemanding - hvem er hvor i dag, og hvem har fri
          {showStaffing && (
            <span className="tiny muted" style={{ display: 'block' }}>
              Sagerne får halvdelen af skærmen. Er der ikke plads til dem alle, viser skærmen selv
              "+ N flere sager" nederst.
            </span>
          )}
        </span>
      </label>

      {showStaffing && (
        <div className="field" style={{ marginBottom: 14 }}>
          <span className="field__label">Hvem vises i dagens bemanding</span>
          <span className="tiny muted">
            {chosenStaffing.length === 0
              ? 'Ingen valgt - alle vises. Sæt flueben ved dem, der skal stå på skærmen.'
              : `${chosenStaffing.length} valgt. Der er plads til 11 på skærmen - resten står som "+ N flere".`}
          </span>
          <div className="staffing-picker">
            {users
              ?.filter((user) => user.isActive && !user.isHidden)
              .map((user) => (
                <label key={user.id} className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={staffingUserIds.includes(user.id)}
                    onChange={() => toggleStaffingUser(user.id)}
                  />
                  <span className="small">{user.displayName}</span>
                </label>
              ))}
          </div>
          {chosenStaffing.length > 0 && (
            <div>
              <button
                className="btn btn--sm"
                type="button"
                onClick={() => {
                  setStaffingUserIds([])
                  setSaved(false)
                }}
              >
                Vis alle igen
              </button>
            </div>
          )}
        </div>
      )}

      <div className="field">
        <span className="field__label">Filtre</span>
        {filters.length === 0 && <div className="small muted">Ingen filtre - dashboardet viser alle aktive projekter.</div>}
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {filters.map((filter, index) => (
            <span key={`${filter.filterType}-${filter.value}-${index}`} className="pill" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
              {filterTypes.find((t) => t.value === filter.filterType)?.label}: {labelFor(filter)}
              <button
                className="banner__close"
                onClick={() => { setFilters(filters.filter((_, i) => i !== index)); setSaved(false) }}
                aria-label="Fjern filter"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="row" style={{ flexWrap: 'wrap' }}>
        <select style={{ maxWidth: 170 }} value={newFilterType} onChange={(e) => { setNewFilterType(e.target.value); setNewFilterValue('') }}>
          {filterTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        {newFilterType === 'category' ? (
          <input style={{ maxWidth: 200 }} placeholder="Kategorinavn" value={newFilterValue} onChange={(e) => setNewFilterValue(e.target.value)} />
        ) : (
          <select style={{ maxWidth: 220 }} value={newFilterValue} onChange={(e) => setNewFilterValue(e.target.value)}>
            <option value="">Vælg ...</option>
            {optionsFor(newFilterType).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        <button
          className="btn btn--sm"
          disabled={!newFilterValue}
          onClick={() => {
            setFilters([...filters, { id: 0, filterType: newFilterType, value: newFilterValue, include: true }])
            setNewFilterValue('')
            setSaved(false)
          }}
        >
          Tilføj filter
        </button>
      </div>

      {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}

      <div className="row" style={{ marginTop: 14 }}>
        <button className="btn btn--primary btn--sm" onClick={() => void save()}>
          Gem
        </button>
        <a className="btn btn--sm" href={`/tv?dashboard=${dashboard.id}`} target="_blank" rel="noreferrer">
          Forhåndsvis TV-visning
        </a>
        {saved && <span className="small muted">Gemt - skærmene henter den nye opsætning.</span>}
      </div>
    </Card>
  )
}
